import type { LogicFilterValue } from '../../fetchSurvey/types/survey';
import { LOGIC_FIELDS } from '../constants/logicFields';
import { LOGIC_OPERATORS, type LogicOperator } from '../constants/logicOperators';
import type { LogicContext, ResolvedFieldValue } from '../types';
import { evaluateCompositeRowFilter } from './evaluateCompositeRowFilter';
import { isMatrixQuestion, isMatrixRowAnswers } from '../fields/matrixQuestionUtils';
import { toNumber } from '../fields/resolveFieldValue';
import { getOptionRank, normalizeRankOrderAnswers } from '../fields/rankOrderUtils';

/**
 * CONTAINS for SELECTED_OPTIONS / multiselect: every listed option must be selected.
 * Other fields (text substring, matrix keys): any listed value may match.
 */
function compareContains(
  actual: ResolvedFieldValue,
  values: LogicFilterValue[],
  requireAllMatches: boolean
): boolean {
  const matches = (value: LogicFilterValue) => {
    if (actual instanceof Set) {
      return actual.has(String(value));
    }
    if (Array.isArray(actual)) {
      return actual.some(item => item === value || String(item) === String(value));
    }
    return actual === value || String(actual) === String(value);
  };

  return requireAllMatches ? values.every(matches) : values.some(matches);
}

function compareNumeric(
  actual: ResolvedFieldValue,
  values: LogicFilterValue[],
  operator: LogicOperator
): boolean {
  const num = toNumber(actual);
  if (num === null) return false;
  const compareTo = toNumber(values[0]);
  if (compareTo === null) return false;

  switch (operator) {
    case LOGIC_OPERATORS.GT:
      return num > compareTo;
    case LOGIC_OPERATORS.GTE:
      return num >= compareTo;
    case LOGIC_OPERATORS.LT:
      return num < compareTo;
    case LOGIC_OPERATORS.LTE:
      return num <= compareTo;
    case LOGIC_OPERATORS.EQUALS:
      return num === compareTo;
    case LOGIC_OPERATORS.NOT_EQUALS:
      return num !== compareTo;
    default:
      return false;
  }
}

/** Scalar EQUALS: numeric coercion, then strict / string equality. */
function valuesMatch(actual: ResolvedFieldValue, values: LogicFilterValue[]): boolean {
  if (values.length === 0) return false;

  return (
    compareNumeric(actual, values, LOGIC_OPERATORS.EQUALS) ||
    actual === values[0] ||
    String(actual) === String(values[0])
  );
}

function compareTextEquals(actual: ResolvedFieldValue, values: LogicFilterValue[], caseSensitive: boolean): boolean {
  if (typeof actual !== 'string' || values.length === 0) return false;
  const expected = String(values[0]);
  return caseSensitive ? actual === expected : actual.toLowerCase() === expected.toLowerCase();
}

function compareTextNotEquals(actual: ResolvedFieldValue, values: LogicFilterValue[], caseSensitive: boolean): boolean {
  if (typeof actual !== 'string' || values.length === 0) return false;
  const expected = String(values[0]);
  return caseSensitive ? actual !== expected : actual.toLowerCase() !== expected.toLowerCase();
}

function compareTextContains(actual: ResolvedFieldValue, values: LogicFilterValue[], caseSensitive: boolean): boolean {
  if (typeof actual !== 'string') return false;
  return values.some(value => {
    const needle = String(value);
    return caseSensitive ? actual.includes(needle) : actual.toLowerCase().includes(needle.toLowerCase());
  });
}

function tryCompositeRowFilter(
  field: string | undefined,
  questionId: string | undefined,
  context: LogicContext,
  values: LogicFilterValue[],
  operator: LogicOperator
): boolean | null {
  if (!values.length) return null;
  return evaluateCompositeRowFilter(field, questionId, context, values[0], operator);
}

/** Composite row rules apply only to matrix questions with SLIDER or MATRIX_OPTIONS fields. */
function shouldUseCompositeRowFilter(
  field: string | undefined,
  questionId: string | undefined,
  context: LogicContext
): boolean {
  if (!field || !questionId) return false;
  const isCompositeField = field === LOGIC_FIELDS.SLIDER || field === LOGIC_FIELDS.MATRIX_OPTIONS;
  if (!isCompositeField) return false;
  if (!isMatrixQuestion(context.questionsById.get(questionId))) return false;
  return true;  
}

export function evaluateOperator(
  operator: string,
  actual: ResolvedFieldValue,
  values: LogicFilterValue[],
  field: string | undefined,
  questionId: string | undefined,
  context: LogicContext,
  subConditionOptionId?: string
): boolean {
  switch (operator) {
    case LOGIC_OPERATORS.CONTAINS:
      if (field === LOGIC_FIELDS.TEXT) {
        return compareTextContains(actual, values, false);
      }
      if (field === LOGIC_FIELDS.CASE_SENSITIVE_TEXT) {
        return compareTextContains(actual, values, true);
      }
      if (field === LOGIC_FIELDS.HEATMAP_REGION) {
        return compareContains(actual, values, false);
      }
      return compareContains(actual, values, field === LOGIC_FIELDS.SELECTED_OPTIONS);

    case LOGIC_OPERATORS.DOES_NOT_CONTAIN:
      if (field === LOGIC_FIELDS.TEXT) {
        return !compareTextContains(actual, values, false);
      }
      if (field === LOGIC_FIELDS.CASE_SENSITIVE_TEXT) {
        return !compareTextContains(actual, values, true);
      }
      if (field === LOGIC_FIELDS.HEATMAP_REGION) {
        return !compareContains(actual, values, false);
      }
      return !compareContains(actual, values, field === LOGIC_FIELDS.SELECTED_OPTIONS);

    case LOGIC_OPERATORS.EQUALS: {
      if (shouldUseCompositeRowFilter(field, questionId, context)) {
        const compositeEquals = tryCompositeRowFilter(
          field,
          questionId,
          context,
          values,
          LOGIC_OPERATORS.EQUALS
        );
        if (compositeEquals !== null) return compositeEquals;
      }

      if (field === LOGIC_FIELDS.TEXT) {
        return compareTextEquals(actual, values, false);
      }
      if (field === LOGIC_FIELDS.CASE_SENSITIVE_TEXT) {
        return compareTextEquals(actual, values, true);
      }
      return valuesMatch(actual, values);
    }

    case LOGIC_OPERATORS.NOT_EQUALS: {
      if (shouldUseCompositeRowFilter(field, questionId, context)) {
        const compositeNotEquals = tryCompositeRowFilter(
          field,
          questionId,
          context,
          values,
          LOGIC_OPERATORS.NOT_EQUALS
        );
        if (compositeNotEquals !== null) return compositeNotEquals;
      }

      if (field === LOGIC_FIELDS.TEXT) {
        return compareTextNotEquals(actual, values, false);
      }
      if (field === LOGIC_FIELDS.CASE_SENSITIVE_TEXT) {
        return compareTextNotEquals(actual, values, true);
      }
      return !valuesMatch(actual, values);
    }

    case LOGIC_OPERATORS.GT:
    case LOGIC_OPERATORS.GTE:
    case LOGIC_OPERATORS.LT:
    case LOGIC_OPERATORS.LTE: {
      if (
        shouldUseCompositeRowFilter(field, questionId, context) &&
        isMatrixRowAnswers(actual)
      ) {
        return tryCompositeRowFilter(field, questionId, context, values, operator as LogicOperator) ?? false;
      }
      return compareNumeric(actual, values, operator as LogicOperator);
    }

    case LOGIC_OPERATORS.BEFORE:
    case LOGIC_OPERATORS.AFTER: {
      if (
        field !== LOGIC_FIELDS.RANK_ORDER ||
        !questionId ||
        !subConditionOptionId ||
        values.length === 0
      ) {
        return false;
      }

      const rankAnswers = normalizeRankOrderAnswers(context.answers[questionId]);
      const subjectRank = getOptionRank(rankAnswers, subConditionOptionId);
      const referenceRank = getOptionRank(rankAnswers, String(values[0]));

      if (subjectRank === undefined || referenceRank === undefined) {
        return false;
      }

      return operator === LOGIC_OPERATORS.BEFORE
        ? subjectRank < referenceRank
        : subjectRank > referenceRank;
    }

    default:
      return false;
  }
}
